/**
 * Admin despacho — Portal de Clientes (Materialidad).
 * Cuentas por persona + solicitudes con enlace seguro.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Loader2, Plus, RefreshCw, Search, Send, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MaterialidadProyectosPanel from "@/modules/tramites/materialidad/MaterialidadProyectosPanel";
import {
  MATERIALIDAD_PORTAL_SUB_ACCESO,
  MATERIALIDAD_PORTAL_SUB_ACCESO_LABEL,
  MATERIALIDAD_PORTAL_SUB_DOCUMENTOS,
  MATERIALIDAD_PORTAL_SUB_DOCUMENTOS_LABEL,
  MATERIALIDAD_PORTAL_SUB_PROYECTOS,
  MATERIALIDAD_PORTAL_SUB_PROYECTOS_LABEL,
} from "@/modules/tramites/materialidad/materialidadConstants";
import {
  createPortalDocRequest,
  createPortalUser,
  fetchPortalDocRequestDetail,
  fetchPortalDocRequests,
  fetchPortalDocumentTypes,
  fetchPortalUsers,
  resetPortalUserPassword,
  reviewPortalRequestItem,
  setPortalUserStatus,
} from "@/modules/tramites/materialidad/portalApi";

function originBase() {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

function clientLabel(cli) {
  if (!cli) return "—";
  return cli.razon_social || cli.nombre_comercial || cli.nombre || cli.rfc || cli.id;
}

export default function MaterialidadPortalPanel({ clientId, clients = [] }) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [workClientId, setWorkClientId] = useState(clientId || "");
  const [companySearch, setCompanySearch] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [extraClientIds, setExtraClientIds] = useState([]);
  const [creatingUser, setCreatingUser] = useState(false);
  const [lastCredentials, setLastCredentials] = useState(null);

  const [reqTitle, setReqTitle] = useState("Solicitud de documentación");
  const [reqMessage, setReqMessage] = useState("");
  const [reqTypes, setReqTypes] = useState([]);
  const [creatingReq, setCreatingReq] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const clientOptions = useMemo(
    () => (Array.isArray(clients) ? clients : []),
    [clients],
  );

  useEffect(() => {
    if (clientId) setWorkClientId(clientId);
  }, [clientId]);

  const companySearchNeedle = companySearch.trim().toLowerCase();

  const clientsForSelect = useMemo(() => {
    let rows = clientOptions;
    if (companySearchNeedle) {
      rows = clientOptions.filter((c) => {
        const label = clientLabel(c).toLowerCase();
        const rfc = String(c?.rfc || "").toLowerCase();
        return label.includes(companySearchNeedle) || rfc.includes(companySearchNeedle);
      });
    }
    if (workClientId && !rows.some((c) => c.id === workClientId)) {
      const selected = clientOptions.find((c) => c.id === workClientId);
      if (selected) rows = [selected, ...rows];
    }
    return rows;
  }, [clientOptions, companySearchNeedle, workClientId]);

  const selectedClient = useMemo(
    () => clientOptions.find((c) => c.id === workClientId) || null,
    [clientOptions, workClientId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, dt] = await Promise.all([
        fetchPortalUsers(),
        fetchPortalDocRequests(workClientId || undefined),
        fetchPortalDocumentTypes(),
      ]);
      setUsers(u?.items || []);
      setRequests(r?.items || []);
      setDocTypes(dt?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el portal de clientes.");
    } finally {
      setLoading(false);
    }
  }, [workClientId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExtraClient = (id, checked) => {
    setExtraClientIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const toggleReqType = (id, checked) => {
    setReqTypes((prev) => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const handleCreateUser = async () => {
    if (!workClientId) {
      toast.error("Seleccione una empresa.");
      return;
    }
    const clientIds = Array.from(new Set([workClientId, ...extraClientIds]));
    setCreatingUser(true);
    try {
      const data = await createPortalUser({
        name,
        email,
        phone,
        client_ids: clientIds,
        role: "responsible",
      });
      setLastCredentials(
        data.temporary_password
          ? {
              login_code: data.login_code,
              email: data.email,
              temporary_password: data.temporary_password,
            }
          : null,
      );
      toast.success(
        data.created_new
          ? "Cuenta de portal creada. Entregue las credenciales temporales al cliente."
          : "Empresas agregadas a la cuenta existente.",
      );
      setName("");
      setEmail("");
      setPhone("");
      setExtraClientIds([]);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el acceso.");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!workClientId) {
      toast.error("Seleccione una empresa.");
      return;
    }
    if (!reqTypes.length) {
      toast.error("Seleccione al menos un tipo de documento.");
      return;
    }
    setCreatingReq(true);
    try {
      const data = await createPortalDocRequest({
        client_id: workClientId,
        title: reqTitle,
        message: reqMessage,
        document_types: reqTypes,
        days_valid: 14,
      });
      const path = data.secure_link_path || "";
      const full = `${originBase()}${path}`;
      setLastLink(full);
      toast.success("Solicitud creada. Copie el enlace seguro para el cliente.");
      setReqMessage("");
      setReqTypes([]);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la solicitud.");
    } finally {
      setCreatingReq(false);
    }
  };

  const openRequest = async (id) => {
    try {
      const data = await fetchPortalDocRequestDetail(id);
      setSelectedRequest(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo abrir la solicitud.");
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado al portapapeles.");
    } catch {
      toast.message(text);
    }
  };

  const renderCompanyPicker = () => (
    <div
      className="rounded-sm border border-slate-200 bg-white p-4 space-y-3"
      data-testid="materialidad-portal-company-picker"
    >
      <p className="text-xs font-semibold text-slate-800 m-0">Empresa</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1 min-w-[200px] max-w-sm flex-1">
          <Label className="text-xs text-slate-600" htmlFor="materialidad-portal-company-search">
            Buscar por nombre
          </Label>
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none"
              aria-hidden
            />
            <Input
              id="materialidad-portal-company-search"
              className="h-9 rounded-sm pl-8"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              placeholder="Nombre o RFC…"
              data-testid="materialidad-portal-company-search"
            />
          </div>
        </div>
        <div className="space-y-1 min-w-[220px] max-w-md flex-1">
          <Label className="text-xs text-slate-600">Seleccionar empresa</Label>
          <Select
            value={workClientId || "__none__"}
            onValueChange={(v) => setWorkClientId(v === "__none__" ? "" : v)}
          >
            <SelectTrigger className="h-9 rounded-sm" data-testid="materialidad-portal-client-select">
              <SelectValue placeholder="Elija una empresa…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Elija una empresa —</SelectItem>
              {clientsForSelect.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {clientLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {companySearchNeedle && clientsForSelect.length === 0 ? (
        <p className="text-[10px] text-amber-800 m-0">Ninguna empresa coincide con la búsqueda.</p>
      ) : null}
      {selectedClient ? (
        <p className="text-[11px] text-slate-600 m-0" data-testid="materialidad-portal-selected-client">
          Activa: <span className="font-semibold text-[#0F2D5C]">{clientLabel(selectedClient)}</span>
          {selectedClient.rfc ? ` · ${selectedClient.rfc}` : ""}
        </p>
      ) : (
        <p className="text-[11px] text-amber-900/90 m-0">
          Seleccione una empresa para administrar accesos y documentos del proveedor.
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="materialidad-portal-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[#0F2D5C] flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Portal de clientes
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Accesos del cliente, documentos del proveedor y proyectos de obra en un solo lugar.
            El cliente podrá cargar evidencias según lo negociado; el despacho también puede cargarlas.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-sm"
          onClick={load}
          disabled={loading || !workClientId}
          data-testid="materialidad-portal-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Actualizar</span>
        </Button>
      </div>

      <Tabs defaultValue={MATERIALIDAD_PORTAL_SUB_ACCESO} className="space-y-4" data-testid="materialidad-portal-subtabs">
        <TabsList className="rounded-sm flex flex-wrap h-auto gap-1 bg-slate-100/80">
          <TabsTrigger
            value={MATERIALIDAD_PORTAL_SUB_ACCESO}
            className="rounded-sm"
            data-testid="materialidad-portal-subtab-acceso"
          >
            {MATERIALIDAD_PORTAL_SUB_ACCESO_LABEL}
          </TabsTrigger>
          <TabsTrigger
            value={MATERIALIDAD_PORTAL_SUB_DOCUMENTOS}
            className="rounded-sm"
            data-testid="materialidad-portal-subtab-documentos"
          >
            {MATERIALIDAD_PORTAL_SUB_DOCUMENTOS_LABEL}
          </TabsTrigger>
          <TabsTrigger
            value={MATERIALIDAD_PORTAL_SUB_PROYECTOS}
            className="rounded-sm"
            data-testid="materialidad-portal-subtab-proyectos"
          >
            {MATERIALIDAD_PORTAL_SUB_PROYECTOS_LABEL}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={MATERIALIDAD_PORTAL_SUB_ACCESO} className="space-y-4 mt-0">
          {renderCompanyPicker()}
          {!workClientId ? (
            <div
              className="rounded-sm border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
              data-testid="materialidad-portal-need-company"
            >
              Seleccione una empresa arriba para administrar accesos del proveedor.
            </div>
          ) : (
            <>
              {lastCredentials && (
                <div
                  className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-sm space-y-1"
                  data-testid="materialidad-portal-temp-credentials"
                >
                  <p className="font-semibold text-sky-950">Credenciales temporales (mostrar una sola vez)</p>
                  <p>Código: <code>{lastCredentials.login_code}</code></p>
                  <p>Correo: <code>{lastCredentials.email}</code></p>
                  <p>Contraseña temporal: <code>{lastCredentials.temporary_password}</code></p>
                  <p className="text-xs text-sky-800">Portal: {originBase()}/portal-materialidad</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm mt-2"
                    onClick={() =>
                      copyText(
                        `Portal: ${originBase()}/portal-materialidad\nCódigo: ${lastCredentials.login_code}\nCorreo: ${lastCredentials.email}\nContraseña: ${lastCredentials.temporary_password}`,
                      )
                    }
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                  </Button>
                </div>
              )}

              <section className="space-y-3 rounded-sm border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Plus className="w-4 h-4" /> {MATERIALIDAD_PORTAL_SUB_ACCESO_LABEL}
                </h3>
                <p className="text-xs text-slate-500 m-0">
                  Cuentas por persona (código CLI-BNT + correo) con acceso a una o varias empresas.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="portal-name">Nombre</Label>
                    <Input id="portal-name" className="rounded-sm" value={name} onChange={(e) => setName(e.target.value)} data-testid="materialidad-portal-name" />
                  </div>
                  <div>
                    <Label htmlFor="portal-email">Correo</Label>
                    <Input id="portal-email" type="email" className="rounded-sm" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="materialidad-portal-email" />
                  </div>
                  <div>
                    <Label htmlFor="portal-phone">Teléfono (opcional)</Label>
                    <Input id="portal-phone" className="rounded-sm" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Otras empresas del mismo responsable</Label>
                  <div className="grid gap-2 sm:grid-cols-2 max-h-40 overflow-y-auto">
                    {clientOptions
                      .filter((c) => c.id !== workClientId)
                      .map((c) => (
                        <label key={c.id} className="flex items-center gap-2 text-xs text-slate-700">
                          <Checkbox
                            checked={extraClientIds.includes(c.id)}
                            onCheckedChange={(v) => toggleExtraClient(c.id, !!v)}
                          />
                          <span className="truncate">{clientLabel(c)} · {c.rfc}</span>
                        </label>
                      ))}
                  </div>
                </div>
                <Button
                  type="button"
                  className="rounded-sm"
                  disabled={creatingUser || !name.trim() || !email.trim()}
                  onClick={handleCreateUser}
                  data-testid="materialidad-portal-create-user"
                >
                  {creatingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Crear / agregar acceso
                </Button>
              </section>

              <section className="space-y-3 rounded-sm border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-800">Usuarios del portal</h3>
                {loading && !users.length ? (
                  <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</p>
                ) : users.length === 0 ? (
                  <p className="text-sm text-slate-500">Aún no hay cuentas de portal.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="materialidad-portal-users-table">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b">
                          <th className="py-2 pr-2">Nombre</th>
                          <th className="py-2 pr-2">Código</th>
                          <th className="py-2 pr-2">Correo</th>
                          <th className="py-2 pr-2">Empresas</th>
                          <th className="py-2 pr-2">Estado</th>
                          <th className="py-2">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} className="border-b border-slate-100 align-top">
                            <td className="py-2 pr-2">{u.name}</td>
                            <td className="py-2 pr-2 font-mono text-xs">{u.login_code}</td>
                            <td className="py-2 pr-2">{u.email}</td>
                            <td className="py-2 pr-2 text-xs">
                              {(u.accesses || []).map((a) => (
                                <div key={a.id}>{a.client_name || a.client_id}</div>
                              ))}
                            </td>
                            <td className="py-2 pr-2">{u.status}</td>
                            <td className="py-2 space-x-1">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="rounded-sm h-7 text-xs"
                                onClick={async () => {
                                  try {
                                    const data = await resetPortalUserPassword(u.id);
                                    setLastCredentials({
                                      login_code: data.login_code || u.login_code,
                                      email: data.email || u.email,
                                      temporary_password: data.temporary_password,
                                    });
                                    toast.success("Contraseña temporal regenerada.");
                                    await load();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "No se pudo restablecer.");
                                  }
                                }}
                              >
                                Restablecer
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="rounded-sm h-7 text-xs"
                                onClick={async () => {
                                  const next = u.status === "blocked" ? "active" : "blocked";
                                  try {
                                    await setPortalUserStatus(u.id, next);
                                    toast.success(next === "blocked" ? "Cuenta bloqueada." : "Cuenta reactivada.");
                                    await load();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "No se pudo cambiar el estado.");
                                  }
                                }}
                              >
                                {u.status === "blocked" ? "Desbloquear" : "Bloquear"}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </TabsContent>

        <TabsContent value={MATERIALIDAD_PORTAL_SUB_DOCUMENTOS} className="space-y-4 mt-0">
          {renderCompanyPicker()}
          {!workClientId ? (
            <div
              className="rounded-sm border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
              data-testid="materialidad-portal-docs-need-company"
            >
              Seleccione una empresa arriba para ver y crear solicitudes de documentos.
            </div>
          ) : (
            <>
              {lastLink && (
                <div
                  className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm"
                  data-testid="materialidad-portal-last-link"
                >
                  <p className="font-semibold text-emerald-950">Enlace seguro de la última solicitud</p>
                  <p className="break-all mt-1"><code>{lastLink}</code></p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm mt-2"
                    onClick={() => copyText(lastLink)}
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copiar enlace
                  </Button>
                </div>
              )}

              <section className="space-y-3 rounded-sm border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Send className="w-4 h-4" /> Nueva solicitud — {MATERIALIDAD_PORTAL_SUB_DOCUMENTOS_LABEL}
                </h3>
                <p className="text-xs text-slate-500 m-0">
                  Genere un enlace para que el cliente (o el despacho) cargue evidencias del proveedor según lo negociado.
                </p>
                <div>
                  <Label htmlFor="portal-req-title">Título</Label>
                  <Input id="portal-req-title" className="rounded-sm" value={reqTitle} onChange={(e) => setReqTitle(e.target.value)} data-testid="materialidad-portal-req-title" />
                </div>
                <div>
                  <Label htmlFor="portal-req-msg">Mensaje al cliente</Label>
                  <Textarea id="portal-req-msg" className="rounded-sm" rows={3} value={reqMessage} onChange={(e) => setReqMessage(e.target.value)} />
                </div>
                <div>
                  <Label className="mb-2 block">Documentos a solicitar</Label>
                  <div className="grid gap-2 sm:grid-cols-2 max-h-48 overflow-y-auto">
                    {docTypes.map((d) => (
                      <label key={d.id} className="flex items-center gap-2 text-xs text-slate-700">
                        <Checkbox
                          checked={reqTypes.includes(d.id)}
                          onCheckedChange={(v) => toggleReqType(d.id, !!v)}
                        />
                        <span>{d.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button
                  type="button"
                  className="rounded-sm"
                  disabled={creatingReq || !reqTitle.trim()}
                  onClick={handleCreateRequest}
                  data-testid="materialidad-portal-create-request"
                >
                  {creatingReq ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Crear solicitud y enlace
                </Button>
              </section>

              <section className="space-y-3 rounded-sm border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-800">Solicitudes de la empresa</h3>
                {requests.length === 0 ? (
                  <p className="text-sm text-slate-500">Sin solicitudes aún.</p>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="materialidad-portal-requests-list">
                    {requests.map((r) => (
                      <li key={r.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{r.title}</p>
                          <p className="text-xs text-slate-500">
                            {r.status} · pendientes {r.pending_items}/{r.total_items}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {r.secure_link_path && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="rounded-sm h-7 text-xs"
                              onClick={() => copyText(`${originBase()}${r.secure_link_path}`)}
                            >
                              Copiar enlace
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            className="rounded-sm h-7 text-xs"
                            onClick={() => openRequest(r.id)}
                            data-testid={`materialidad-portal-open-req-${r.id}`}
                          >
                            Revisar
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {selectedRequest && (
                  <div className="mt-3 rounded-sm border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="materialidad-portal-request-detail">
                    <p className="text-sm font-semibold">{selectedRequest.request?.title}</p>
                    <p className="text-xs text-slate-600">{selectedRequest.request?.message}</p>
                    {(selectedRequest.items || []).map((it) => (
                      <div key={it.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-2">
                        <div className="text-xs">
                          <p className="font-medium">{it.document_type_label || it.document_type}</p>
                          <p className="text-slate-500">{it.status}{it.filename ? ` · ${it.filename}` : ""}</p>
                          {it.file_id && (
                            <a
                              className="text-[#0F2D5C] underline"
                              href={`/api/tramites/materialidad/files/${encodeURIComponent(it.file_id)}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Ver archivo
                            </a>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="rounded-sm h-7 text-xs"
                            disabled={!it.file_id}
                            onClick={async () => {
                              try {
                                await reviewPortalRequestItem(it.id, { status: "accepted" });
                                toast.success("Documento aceptado.");
                                await openRequest(selectedRequest.request.id);
                                await load();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Error al revisar.");
                              }
                            }}
                          >
                            Aceptar
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="rounded-sm h-7 text-xs"
                            disabled={!it.file_id}
                            onClick={async () => {
                              const observation = window.prompt("Observación para el cliente:", it.observation || "") || "";
                              try {
                                await reviewPortalRequestItem(it.id, { status: "observed", observation });
                                toast.success("Documento observado.");
                                await openRequest(selectedRequest.request.id);
                                await load();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Error al observar.");
                              }
                            }}
                          >
                            Observar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </TabsContent>

        <TabsContent value={MATERIALIDAD_PORTAL_SUB_PROYECTOS} className="mt-0" data-testid="materialidad-portal-proyectos">
          <MaterialidadProyectosPanel clientId={workClientId || clientId} clients={clients} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
